import { redirect, type RouteObject } from 'react-router';

import { ContinuityEscrowOnboardingRoutesEnum } from 'src/pages/ContinuityEscrowOnboarding';
import { EscrowOnboardingNewRoutesEnum } from 'src/pages/EscrowOnboardingAccess/routes.enum';
import { ContinuityEscrowRoutesEnum, ContinuityEscrowSettingsRoutesEnum } from 'src/pages/ContinuityEscrow';
import { ContinuityEscrowRequestChangesRoutesEnum } from 'src/pages/ContinuityEscrowRequestChanges';

export const continuityEscrowsOnboardingRoutes: RouteObject = {
  path: 'continuity-escrows/onboarding',
  children: [
    {
      path: 'new/:appId',
      lazy: async () => {
        const { default: Component } = await import('src/pages/ContinuityEscrowOnboarding/New');

        return { Component };
      },
    },
    {
      path: `new/:appId/${EscrowOnboardingNewRoutesEnum.Access}`,
      lazy: async () => {
        const { default: Component } = await import('src/pages/EscrowOnboardingAccess');

        return { Component };
      },
    },
    {
      path: ':id/*',
      lazy: async () => {
        const { Layout } = await import('src/pages/ContinuityEscrowOnboarding/ui');

        return { Component: Layout };
      },
      children: [
        {
          path: ContinuityEscrowOnboardingRoutesEnum.Members,
          lazy: async () => {
            const { default: Component } = await import('src/pages/ContinuityEscrowOnboarding/Members');

            return { Component };
          },
        },
        {
          path: ContinuityEscrowOnboardingRoutesEnum.Depositor,
          lazy: async () => {
            const { default: Component } = await import('src/pages/ContinuityEscrowOnboarding/Depositor');

            return { Component };
          },
        },
        {
          path: ContinuityEscrowOnboardingRoutesEnum.Beneficiary,
          lazy: async () => {
            const { default: Component } = await import('src/pages/ContinuityEscrowOnboarding/Beneficiary');

            return { Component };
          },
        },
        {
          path: ContinuityEscrowOnboardingRoutesEnum.NotificationSettings,
          lazy: async () => {
            const { default: Component } = await import('src/pages/ContinuityEscrowOnboarding/NotificationSettings');

            return { Component };
          },
        },
        {
          path: ContinuityEscrowOnboardingRoutesEnum.ActivationNotifications,
          lazy: async () => {
            const { default: Component } = await import('src/pages/ContinuityEscrowOnboarding/ActivationNotifications');

            return { Component };
          },
        },
        {
          path: ContinuityEscrowOnboardingRoutesEnum.EnvironmentVisibility,
          lazy: async () => {
            const { default: Component } = await import('src/pages/ContinuityEscrowOnboarding/EnvironmentVisibility');

            return { Component };
          },
        },
        {
          path: ContinuityEscrowOnboardingRoutesEnum.AgreementSetup,
          lazy: async () => {
            const { default: Component } = await import('src/pages/ContinuityEscrowOnboarding/AgreementSetup');

            return { Component };
          },
        },
        {
          path: ContinuityEscrowOnboardingRoutesEnum.AgreementDetails,
          lazy: async () => {
            const { default: Component } = await import('src/pages/ContinuityEscrowOnboarding/AgreementDetails');

            return { Component };
          },
        },
        {
          path: ContinuityEscrowOnboardingRoutesEnum.Summary,
          lazy: async () => {
            const { default: Component } = await import('src/pages/ContinuityEscrowOnboarding/Summary');

            return { Component };
          },
        },
      ],
    },
  ],
};

export const continuityEscrowRoutes: RouteObject = {
  path: 'continuity-escrows/:id/*',
  lazy: async () => {
    const { ContinuityEscrowLayout } = await import('src/pages/ContinuityEscrow/ui');

    return { Component: ContinuityEscrowLayout };
  },
  loader: ({ params }) => {
    const { id, '*': path = '' } = params;

    if (!path) return redirect(`/continuity-escrows/${id}/${ContinuityEscrowRoutesEnum.Overview}`);

    if (
      !Object.values(ContinuityEscrowRoutesEnum).includes(path as ContinuityEscrowRoutesEnum) &&
      !Object.values(ContinuityEscrowSettingsRoutesEnum).includes(path as ContinuityEscrowSettingsRoutesEnum)
    )
      return redirect('/404');

    return null;
  },
  children: [
    {
      path: ContinuityEscrowRoutesEnum.Overview,
      lazy: async () => {
        const { default: Component } = await import('src/pages/ContinuityEscrow/Overview');

        return { Component };
      },
    },
    {
      path: ContinuityEscrowRoutesEnum.Agreement,
      lazy: async () => {
        const { default: Component } = await import('src/pages/ContinuityEscrow/Agreement');

        return { Component };
      },
    },
    {
      path: ContinuityEscrowRoutesEnum.Environment,
      lazy: async () => {
        const { default: Component } = await import('src/pages/ContinuityEscrow/Environment');

        return { Component };
      },
    },
    {
      path: ContinuityEscrowRoutesEnum.Reports,
      lazy: async () => {
        const { default: Component } = await import('src/pages/ContinuityEscrow/Reports');

        return { Component };
      },
    },
    {
      path: ContinuityEscrowRoutesEnum.Members,
      lazy: async () => {
        const { default: Component } = await import('src/pages/ContinuityEscrow/Members');

        return { Component };
      },
    },
    {
      path: `${ContinuityEscrowRoutesEnum.Settings}/*`,
      lazy: async () => {
        const { SettingsLayout: Component } = await import('src/pages/ContinuityEscrow/Settings');

        return { Component };
      },
      loader: ({ params }) => {
        const { id, '*': path = '' } = params;

        const defaultLink = `/continuity-escrows/${id}/${ContinuityEscrowRoutesEnum.Settings}/${ContinuityEscrowSettingsRoutesEnum.Depositor}`;
        const isValidPath = Object.values(ContinuityEscrowSettingsRoutesEnum).includes(
          path as ContinuityEscrowSettingsRoutesEnum,
        );

        if (!path || !isValidPath) return redirect(defaultLink);

        return null;
      },
      children: [
        {
          path: ContinuityEscrowSettingsRoutesEnum.Agreement,
          lazy: async () => {
            const { SettingsAgreement: Component } = await import('src/pages/ContinuityEscrow/Settings');

            return { Component };
          },
        },
        {
          path: ContinuityEscrowSettingsRoutesEnum.Beneficiary,
          lazy: async () => {
            const { SettingsBeneficiary: Component } = await import('src/pages/ContinuityEscrow/Settings');

            return { Component };
          },
        },
        {
          path: ContinuityEscrowSettingsRoutesEnum.Depositor,
          lazy: async () => {
            const { SettingsDepositor: Component } = await import('src/pages/ContinuityEscrow/Settings');

            return { Component };
          },
        },
        {
          path: ContinuityEscrowSettingsRoutesEnum.Technical,
          lazy: async () => {
            const { SettingsTechnical: Component } = await import('src/pages/ContinuityEscrow/Settings');

            return { Component };
          },
        },
      ],
    },
  ],
};

export const continuityEscrowsRequestChangesRoutes: RouteObject = {
  path: 'continuity-escrows/:id/request-changes/*',
  lazy: async () => {
    const { ContinuityEscrowRequestChangesLayout } = await import('src/pages/ContinuityEscrowRequestChanges');

    return { Component: ContinuityEscrowRequestChangesLayout };
  },
  children: [
    {
      path: ContinuityEscrowRequestChangesRoutesEnum.Agreement,
      lazy: async () => {
        const { default: Component } = await import('src/pages/ContinuityEscrowRequestChanges/Agreement');

        return { Component };
      },
    },
    {
      path: ContinuityEscrowRequestChangesRoutesEnum.Depositor,
      lazy: async () => {
        const { default: Component } = await import('src/pages/ContinuityEscrowRequestChanges/Depositor');

        return { Component };
      },
    },
    {
      path: ContinuityEscrowRequestChangesRoutesEnum.Beneficiary,
      lazy: async () => {
        const { default: Component } = await import('src/pages/ContinuityEscrowRequestChanges/Beneficiary');

        return { Component };
      },
    },
    {
      path: ContinuityEscrowRequestChangesRoutesEnum.Technical,
      lazy: async () => {
        const { default: Component } = await import('src/pages/ContinuityEscrowRequestChanges/Technical');

        return { Component };
      },
    },
    {
      path: ContinuityEscrowRequestChangesRoutesEnum.Environment,
      lazy: async () => {
        const { default: Component } = await import('src/pages/ContinuityEscrowRequestChanges/Environment');

        return { Component };
      },
    },
  ],
};
